import React from 'react';
import { Navigate } from 'react-router-dom';
import { useUser } from '../context';
import { SocialLoginCard } from '../SocialLoginCard';
import { ACCOUNT_BASE_PATH } from './accountPaths';

/**
 * Dedicated sign-in page. Emphasizes that auth is delegated to Google / Microsoft
 * and that OnTime does not keep passwords or other login secrets.
 */
const LoginPage = () => {
  const { user, status } = useUser();

  if (status === 'loading') {
    return (
      <main className="relative z-0 mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <p className="font-sans text-sm text-ink/60" role="status">
          Loading…
        </p>
      </main>
    );
  }

  if (status === 'ready' && user !== null) {
    return <Navigate to={ACCOUNT_BASE_PATH} replace />;
  }

  return (
    <main className="relative z-0 mx-auto max-w-6xl px-4 py-10 sm:px-6">
      <div className="mb-8 max-w-2xl">
        <p className="inline-flex rounded-full bg-skysoft/50 px-3 py-1 font-sans text-xs font-semibold uppercase tracking-wider text-ink/70">
          Sign in
        </p>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-tight text-ink sm:text-5xl">
          Log in safely
        </h1>
        <p className="mt-4 font-sans text-lg text-ink/70">
          Free to create an account. Sign in with the account you already trust —
          Google or Microsoft.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
        <div className="space-y-6 lg:col-span-7">
          <div
            className="rounded-3xl border-[3px] border-ink/15 bg-skysoft/40 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
            role="note"
          >
            <h2 className="font-display text-xl font-semibold text-ink">
              Your login stays with Google &amp; Microsoft
            </h2>
            <p className="mt-2 font-sans text-sm leading-relaxed text-ink/75">
              OnTime does not keep any login secrets. We never ask for or store your
              password, and we do not hold Google or Microsoft credentials on our
              servers. Sign-in is handled entirely by Google or Microsoft — we only
              receive the basic profile info needed for your account after you
              approve it.
            </p>
            <ul className="mt-4 list-disc space-y-2 pl-5 font-sans text-sm leading-relaxed text-ink/75">
              <li>No passwords stored by OnTime</li>
              <li>No secret keys or login tokens kept for signing you in</li>
              <li>All authentication runs through Google or Microsoft</li>
            </ul>
          </div>

          <section className="rounded-3xl border-2 border-dashed border-ink/15 bg-white/40 p-6">
            <h2 className="font-display text-lg font-semibold text-ink">
              How it works
            </h2>
            <p className="mt-2 font-sans text-sm leading-relaxed text-ink/70">
              Choose Google or Microsoft on the right. You&apos;ll complete sign-in
              on their secure page, then return here to your account. Free to create
              an account — no separate OnTime password to remember.
            </p>
          </section>
        </div>

        <div className="lg:col-span-5">
          <SocialLoginCard
            heading="Continue"
            description="Sign in with Google or Microsoft. We never see your password."
            returnPath={ACCOUNT_BASE_PATH}
          />
        </div>
      </div>
    </main>
  );
};

export default LoginPage;
